import { i18n } from "../util/useT.ts";
import { helpers } from "../util/helpers.ts";
import { logEvent } from "../util/logEvent.ts";
import { getReSignAllSuccessToastText } from "../util/successToast.ts";
import { toWorker } from "../util/toWorker.ts";
import type { View } from "../../../../common/types.ts";
import { Mood } from "../components/Mood.tsx";
import { NegotiateButtons } from "../components/NegotiateButtons.tsx";
import { PlayerCardList } from "../components/PlayerCardList.tsx";
import {
  MobileActionableEmptyState,
  MobileContentHero,
  MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import { getRosterPositionNeeds, RosterComposition } from "../components/RosterComposition.tsx";
import { confirm } from "../util/confirm.tsx";
import { decisionConfirmVariants } from "../util/decisionConfirmVariants.ts";
import { NegotiationModal, useNegotiaionModal } from "../components/NegotiationModal.tsx";
import { MobileStatusMetric } from "../components/MobileStatusMetric.tsx";
import { NegotiationMarketRef } from "./negotiationMarketRef.tsx";
import { MarketRatingStatusMetric } from "./MarketRatingStatusMetric.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { createMobileReturnUrl } from "../util/mobilePageContext.ts";
import {
  type NegotiationGroup,
  canFitNegotiationContract,
  getNegotiationAskingAmount,
  groupNegotiationPlayers,
} from "./negotiationTasking.ts";
import {
  type ContractQueueFitState,
  getContractQueueFitStatus,
} from "./TeamFinances/financeStatusMetrics.ts";

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

const createNegotiationPlayerHref = (pid: number, group: NegotiationGroup) =>
  createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
    activeTab: group,
    filterKey: "negotiationGroup",
    filterValue: group,
    pageID: "negotiationList",
    returnLabel: i18n.t("negotiationList.returnToNegotiations", {
      ns: "ui",
      defaultValue: "Return to negotiations",
    }),
    scrollY: getMobileScrollY(),
  });

const groupTitle = (group: NegotiationGroup) => {
  if (group === "must") {
    return i18n.t("negotiationList.groupMust", {
      ns: "ui",
      defaultValue: "Must handle",
    });
  }
  if (group === "suggested") {
    return i18n.t("negotiationList.groupSuggested", {
      ns: "ui",
      defaultValue: "Suggested re-sign",
    });
  }
  return i18n.t("negotiationList.groupWalk", {
    ns: "ui",
    defaultValue: "Can walk",
  });
};

const contractQueueFitLabel = (state: ContractQueueFitState) => {
  if (state === "over") {
    return i18n.t("negotiationList.capFit.over", {
      ns: "ui",
      defaultValue: "Total asking exceeds hard-cap room",
    });
  }
  if (state === "tight") {
    return i18n.t("negotiationList.capFit.tight", {
      ns: "ui",
      defaultValue: "Little hard-cap room would remain",
    });
  }
  if (state === "allowed") {
    return i18n.t("negotiationList.capFit.allowed", {
      ns: "ui",
      defaultValue: "Current rules allow these deals",
    });
  }
  return i18n.t("negotiationList.capFit.fits", {
    ns: "ui",
    defaultValue: "Asking fits within cap space",
  });
};

const NegotiationList = ({
  capSpace,
  challengeNoRatings,
  draftPickAutoContract,
  minContract,
  positionSalary,
  spectator,
  players,
  salaryCapType,
  season,
  sumContracts,
  userPlayers,
}: View<"negotiationList">) => {
  const title =
    salaryCapType === "hard" || !draftPickAutoContract
      ? i18n.t("negotiationList.rookiesTitle", {
          ns: "ui",
          defaultValue: "Rookies and Expiring Contracts",
        })
      : i18n.t("negotiationList.reSignTitle", {
          ns: "ui",
          defaultValue: "Re-sign Players",
        });

  useTitleBar({ title });

  const negotiationModal = useNegotiaionModal();
  const minContractAmount = minContract / 1000;
  const rosterNeedPositions = new Set(getRosterPositionNeeds(userPlayers).map(({ pos }) => pos));

  if (spectator) {
    return (
      <div>
        <p>
          {i18n.t("negotiationList.spectatorMode", {
            ns: "ui",
            defaultValue: "The AI will handle re-signing players in spectator mode.",
          })}
        </p>
      </div>
    );
  }

  const playersByGroup = groupNegotiationPlayers({
    capSpace,
    challengeNoRatings,
    minContractAmount,
    players,
    salaryCapType,
  });
  const mustCount = playersByGroup.must.length;
  const contractQueueFit = getContractQueueFitStatus({
    capSpace,
    salaryCapType,
    totalAsking: sumContracts,
  });

  const renderGroup = (group: NegotiationGroup) => {
    const groupPlayers = playersByGroup[group];
    if (groupPlayers.length === 0) {
      return null;
    }

    return (
      <section
        className="mb-4"
        data-negotiation-group={group}
        data-negotiation-group-count={groupPlayers.length}
        id={group === "must" ? "negotiation-required-list" : undefined}
        key={group}
      >
        <h2 className="h6 mb-2">{groupTitle(group)}</h2>
        <PlayerCardList
          players={groupPlayers}
          season={season}
          shouldShowRatings={() => false}
          getJerseyNumber={(p) => p.jerseyNumber}
          getContract={(p) => ({
            amount: getNegotiationAskingAmount(p),
            exp: p.contract.exp,
          })}
          getPlayerHref={(p) => createNegotiationPlayerHref(p.pid, group)}
          getRole={() => groupTitle(group)}
          renderMetadata={(p) => (
            <>
              {!challengeNoRatings ? (
                <MarketRatingStatusMetric
                  label={i18n.t("marketMetrics.playerLevel", {
                    ns: "ui",
                    defaultValue: "Player level",
                  })}
                  potential={p.ratings.pot}
                  rating={p.ratings.ovr}
                />
              ) : null}
              <div className={challengeNoRatings ? undefined : "mt-2"}>
                <NegotiationMarketRef
                  asking={getNegotiationAskingAmount(p)}
                  pos={p.ratings.pos}
                  positionSalary={positionSalary}
                />
              </div>
            </>
          )}
          getReasonTags={(p) => {
            const tags = [];
            if (p.contract.rookie) {
              tags.push(
                i18n.t("negotiationList.rookie", {
                  ns: "ui",
                  defaultValue: "Rookie",
                }),
              );
            }
            if (p.mood.user.willing) {
              tags.push(
                i18n.t("freeAgents.willing", {
                  ns: "ui",
                  defaultValue: "Willing",
                }),
              );
            } else {
              tags.push(
                i18n.t("freeAgents.refuses", {
                  ns: "ui",
                  defaultValue: "Refuses!",
                }),
              );
            }
            if (rosterNeedPositions.has(p.ratings.pos)) {
              tags.push(
                i18n.t("freeAgents.rosterNeed", {
                  ns: "ui",
                  defaultValue: "Roster need",
                }),
              );
            }
            if (!challengeNoRatings && p.ratings.pot - p.ratings.ovr >= 10) {
              tags.push(
                i18n.t("playerCard.tagHighUpside", {
                  ns: "ui",
                  defaultValue: "High upside",
                }),
              );
            }
            if (
              !canFitNegotiationContract({
                askingAmount: getNegotiationAskingAmount(p),
                capSpace,
                minContractAmount,
                salaryCapType,
              })
            ) {
              tags.push(
                i18n.t("negotiationList.capImpact", {
                  ns: "ui",
                  defaultValue: "Cap impact",
                }),
              );
            }
            return tags.slice(0, 3);
          }}
          renderMood={(p) => (
            <Mood
              defaultType="user"
              host="negotiation-list"
              maxWidth
              p={p}
              playerHref={createNegotiationPlayerHref(p.pid, group)}
            />
          )}
          renderActions={(p) => (
            <div className="d-flex flex-wrap gap-2">
              <NegotiateButtons
                canGoOverCap={salaryCapType === "none" || salaryCapType === "soft"}
                capSpace={capSpace}
                minContract={minContract}
                onNegotiate={async () => {
                  await negotiationModal.negotiate(p.pid);
                }}
                season={season}
                spectator={spectator}
                p={p}
                resigning
                willingToNegotiate={p.mood.user.willing}
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                data-negotiation-cancel-action
                data-negotiation-cancel-pid={p.pid}
                onClick={async () => {
                  const proceed = await confirm(
                    i18n.t("negotiationList.endNegotiationConfirm", {
                      ns: "ui",
                      defaultValue: "End negotiation with this player?",
                    }),
                    {
                      okText: i18n.t("negotiationList.endNegotiation", {
                        ns: "ui",
                        defaultValue: "End negotiation",
                      }),
                      variant: decisionConfirmVariants.negotiationEnd,
                    },
                  );
                  if (proceed) {
                    await toWorker("main", "cancelContractNegotiation", p.pid);
                  }
                }}
              >
                {i18n.t("negotiationList.endNegotiation", {
                  ns: "ui",
                  defaultValue: "End negotiation",
                })}
              </button>
            </div>
          )}
        />
      </section>
    );
  };

  return (
    <div
      data-desktop-production-view="negotiationList"
      data-negotiation-decision-count={players.length}
      data-v5-negotiation-content-first-screen=""
    >
      {players.length > 0 ? (
        // B2c (v5Content): the first screen IS the pending-negotiation queue —
        // decisions/asking/cap-space stat rows and the grouped player list
        // right below. The conclusion/consequence narration card and the
        // core-task template subtitle are gone (法则一); the one explainer
        // line is the deadline, shown only while decisions remain.
        <MobileContentHero
          action={
            mustCount > 0 ? (
              <div>
                <a className="btn btn-primary" href="#negotiation-required-list">
                  {i18n.t("negotiationList.reviewRequired", {
                    ns: "ui",
                    defaultValue: "Review required",
                  })}
                </a>
              </div>
            ) : undefined
          }
          className="mb-3"
          data-v5-negotiation-pending-first-screen=""
          explainer={i18n.t("negotiationList.deadlineBeforeFreeAgency", {
            ns: "ui",
            defaultValue: "Before free agency",
          })}
        >
          <MobileStatRow
            label={i18n.t("negotiationList.expiringCount", {
              ns: "ui",
              defaultValue: "Decisions",
            })}
            tone={mustCount > 0 ? "warning" : "neutral"}
            value={players.length}
          />
          <MobileStatRow
            label={i18n.t("negotiationList.totalAsking", {
              ns: "ui",
              defaultValue: "Total asking",
            })}
            value={helpers.formatCurrency(sumContracts, "M")}
          />
          <MobileStatusMetric
            detail={helpers.formatCurrency(capSpace, "M")}
            label={i18n.t("freeAgents.capSpace", {
              ns: "ui",
              defaultValue: "Cap space",
            })}
            score={contractQueueFit.score}
            value={contractQueueFitLabel(contractQueueFit.state)}
          />
        </MobileContentHero>
      ) : (
        // A3: reason + impact + a single recommended action.
        <div>
          <MobileActionableEmptyState
            action={
              <a className="btn btn-primary" href={helpers.leagueUrl([])}>
                {i18n.t("negotiationList.continueSeason", {
                  ns: "ui",
                  defaultValue: "Continue season",
                })}
              </a>
            }
            reason="empty"
            reasonText={i18n.t("negotiationList.emptyReason", {
              ns: "ui",
              defaultValue: "Every rookie and expiring contract has been handled for this phase.",
            })}
            impact={i18n.t("negotiationList.emptyConsequence", {
              ns: "ui",
              defaultValue: "Market recommendations can focus on other roster moves.",
            })}
            title={i18n.t("negotiationList.emptyConclusion", {
              ns: "ui",
              defaultValue: "No contract decisions are waiting right now.",
            })}
          />
        </div>
      )}

      {(salaryCapType !== "hard" || sumContracts < capSpace) && players.length > 0 ? (
        <button
          className="btn btn-outline-secondary mb-3"
          data-negotiation-resign-all=""
          onClick={async () => {
            const proceed = await confirm(
              i18n.t("negotiationList.reSignAllConfirm", {
                ns: "ui",
                defaultValue: "Are you sure you want to re-sign all {{count}} {{players}}?",
                count: players.length,
                players: i18n.t("negotiationList.playerNoun", {
                  count: players.length,
                  ns: "ui",
                  defaultValue: helpers.plural("player", players.length),
                }),
              }),
              {
                okText: i18n.t("negotiationList.reSignAll", {
                  ns: "ui",
                  defaultValue: "Re-sign all",
                }),
                variant: decisionConfirmVariants.negotiationResignAll,
              },
            );
            if (!proceed) {
              return;
            }

            const reSignCount = players.length;
            const errorMsg = await toWorker("main", "reSignAll", players);

            if (errorMsg) {
              logEvent({
                type: "error",
                text: errorMsg,
                saveToDb: false,
              });
            }

            // N4-2 success feedback: bulk re-sign used to be silent on success
            // (only failures surfaced a red toast). Confirm the outcome with an
            // L1 toast naming how many players were re-signed. The builder
            // returns undefined on failure, keeping the red toast the only
            // feedback there.
            const reSignSuccessText = getReSignAllSuccessToastText({
              errorMsg,
              count: reSignCount,
            });
            if (reSignSuccessText !== undefined) {
              logEvent({
                type: "success",
                text: reSignSuccessText,
                saveToDb: false,
              });
            }
          }}
        >
          {i18n.t("negotiationList.reSignAll", {
            ns: "ui",
            defaultValue: "Re-sign all",
          })}
        </button>
      ) : null}

      {(["must", "suggested", "abandon"] as const).map(renderGroup)}

      {/* Roster-gap context only matters while decisions remain; on the empty
          phase the A3 state stays one message + one action (法则一). */}
      {players.length > 0 ? <RosterComposition className="mb-3" players={userPlayers} /> : null}

      <div className="mt-3">
        <a className="btn btn-link btn-sm px-0" href={helpers.leagueUrl(["market"])}>
          {i18n.t("negotiationList.returnToMarket", {
            ns: "ui",
            defaultValue: "Market home",
          })}
        </a>
      </div>

      <NegotiationModal {...negotiationModal.props} />
    </div>
  );
};

export default NegotiationList;
